import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { APP_STATUS, STATUS } from "@/constants";
import type { AppTabSummary } from "@/features/app-studio/api/appTabsApi";
import { useAppDraft } from "@/features/app-studio/hooks/useAppDraft";
import { useAppTabs, type AppTabsStatus } from "@/features/app-studio/hooks/useAppTabs";
import { fetchApps, type AppsState, type AppSummary } from "@/features/app-studio/model/appsSlice";

export type UseAddToAppTargetResult = {
  apps: AppSummary[];
  appsStatus: AppsState["status"];
  selectedAppId: number | "";
  /**
   * The app id the widget is written to: the selected app's draft version when it is published,
   * so the addition waits for the author to publish it, else the app itself. `null` until known.
   */
  writeAppId: number | null;
  /** Selects an app and clears any tab picked from the previously selected app. */
  selectApp: (appId: number | "") => void;
  tabs: AppTabSummary[];
  tabsStatus: AppTabsStatus;
  tabsError: string | null;
  selectedTabId: number | "";
  selectTab: (tabId: number | "") => void;
  /** True when the selected app has tabs, so the widget's target tab must be chosen. */
  requiresTab: boolean;
  /** True once the app — and its tab when the app has tabs — have been chosen. */
  isTargetComplete: boolean;
  /** The `tab_id` to send with the create payload; null targets an app without tabs. */
  tabIdForPayload: number | null;
  reset: () => void;
};

/**
 * App + tab selection shared by the "add to app" dialogs (table, chart, cards).
 *
 * Tabs are app-scoped, so the tab list reloads and any previous tab choice is
 * dropped whenever the app changes. An app with no tabs keeps its widgets on the
 * implicit default tab, which the API expresses as `tab_id: null`.
 *
 * `enabled` should track the dialog's open state: the apps list is (re)fetched
 * only while the dialog is open.
 */
export function useAddToAppTarget(enabled: boolean): UseAddToAppTargetResult {
  const dispatch = useAppDispatch();
  const apps = useAppSelector((state) => state.apps.apps);
  const appsStatus = useAppSelector((state) => state.apps.status);

  const [selectedAppId, setSelectedAppId] = useState<number | "">("");
  const [selectedTabId, setSelectedTabId] = useState<number | "">("");

  const selectedApp = apps.find((app) => app.id === selectedAppId);
  const isSelectedAppPublished = selectedApp?.status === APP_STATUS.PUBLISHED;
  const {
    draft,
    status: draftStatus,
    error: draftError,
  } = useAppDraft(selectedAppId === "" ? null : selectedAppId, isSelectedAppPublished);
  const writeAppId =
    selectedAppId === ""
      ? null
      : isSelectedAppPublished
        ? (draft?.draft_app_id ?? null)
        : selectedAppId;
  // Tabs come from the app written to: a draft's tabs carry their own ids.
  const {
    tabs,
    status: loadedTabsStatus,
    error: loadedTabsError,
  } = useAppTabs(writeAppId);
  const isDraftFailed = isSelectedAppPublished && draftStatus === STATUS.FAILED;
  const isDraftPending = selectedAppId !== "" && writeAppId === null && !isDraftFailed;
  const tabsStatus = isDraftFailed
    ? STATUS.FAILED
    : isDraftPending
      ? STATUS.LOADING
      : loadedTabsStatus;
  const tabsError = isDraftFailed ? (draftError ?? "Could not open the app's draft.") : loadedTabsError;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (appsStatus !== STATUS.SUCCEEDED || apps.length === 0) {
      void dispatch(fetchApps());
    }
  }, [apps.length, appsStatus, dispatch, enabled]);

  const selectApp = useCallback((appId: number | "") => {
    setSelectedAppId(appId);
    setSelectedTabId("");
  }, []);

  const reset = useCallback(() => {
    setSelectedAppId("");
    setSelectedTabId("");
  }, []);

  const hasSelectedApp = writeAppId !== null;
  // While tabs are loading, `tabs` still holds the previously selected app's tabs.
  const tabsReady = tabsStatus === STATUS.SUCCEEDED;
  const requiresTab = hasSelectedApp && tabsReady && tabs.length > 0;

  return {
    apps,
    appsStatus,
    selectedAppId,
    writeAppId,
    selectApp,
    tabs,
    tabsStatus,
    tabsError,
    selectedTabId,
    selectTab: setSelectedTabId,
    requiresTab,
    isTargetComplete:
      hasSelectedApp && tabsReady && (tabs.length === 0 || selectedTabId !== ""),
    tabIdForPayload: requiresTab && selectedTabId !== "" ? selectedTabId : null,
    reset,
  };
}
